import { Option, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { ContactNotificationType } from '../../../models';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput, SelectOptions } from '../../shared-components/form';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';

interface AdditionalContactDetailFormProps {
  isViewOnly: boolean;
}

const getNotificationOptionLabel = (contactNotificationTypeId: ContactNotificationType) => {
  if (contactNotificationTypeId === ContactNotificationType.AllNotifications) return 'All notifications';
  if (contactNotificationTypeId === ContactNotificationType.OnlyWinnerFinalistNotification) return 'Only winner/finalist notification';
  return '';
};

const AdditionalContactDetailFormImpl: React.FC<AdditionalContactDetailFormProps> = (props) => {
  const {
    isViewOnly,
  } = props;

  const {
    nominationForStaffPageUiStore,
  } = useStores();

  return (
    <FlexBox
      isFullWidth
      as='section'
      className='nomination-details__fields'
    >
      <Text
        as='h3'
        block
        weight='semibold'
      >
        Additional Contact
      </Text>
      <FlexBox
        isFullWidth
        direction='column'
        className='fields__list'
      >
        {
          nominationForStaffPageUiStore.nominationForStaff.nomination.contacts.map((additionalContact, index) => {
            const validation = nominationForStaffPageUiStore.additionalContactValidations[index];
            return (
              <FlexBox
                key={additionalContact.contactId}
                isFullWidth
                direction='column'
                className='fields__list'
              >
              <FlexBox isFullWidth aligns='start'>
                <CommonLabelAndValueList
                  labelId={`prefix-${additionalContact.contactId}`}
                  label='Prefix:'
                  childrenFallback={additionalContact.prefix}
                >
                  {
                    !isViewOnly &&
                    <SelectOptions
                      onOptionSelect={(data) => additionalContact.prefix = data.optionValue ? data.optionValue : ''}
                      validationMessage={
                        validation.fieldErrors.prefix?.length
                          ? <ValidationMessageList messages={validation.fieldErrors.prefix} />
                          : undefined
                      }
                      dropdownProps={{
                        'aria-labelledby': `prefix-${additionalContact.contactId}`,
                        value: additionalContact.prefix ? additionalContact.prefix.trim() : '',
                        selectedOptions: [additionalContact.prefix ? additionalContact.prefix.trim() : ''],
                      }}
                    >
                      <Option text='' />
                      <Option value='Mr.'>Mr.</Option>
                      <Option value='Mrs.'>Mrs.</Option>
                      <Option value='Ms.'>Ms.</Option>
                    </SelectOptions>
                  }
                </CommonLabelAndValueList>
                <CommonLabelAndValueList
                  labelId={`first-name-${additionalContact.contactId}`}
                  label='First name:'
                  childrenFallback={additionalContact.firstName}
                >
                  {
                    !isViewOnly &&
                    <FormInput
                      validationMessage={
                        validation.fieldErrors.firstName?.length
                          ? <ValidationMessageList messages={validation.fieldErrors.firstName} />
                          : undefined
                      }
                      onChange={firstName => additionalContact.firstName = firstName}
                      inputProps={{
                        'aria-labelledby': `first-name-${additionalContact.contactId}`,
                        value: additionalContact.firstName,
                        maxLength: 100,
                      }}
                    />
                  }
                </CommonLabelAndValueList>
              </FlexBox>
              <FlexBox isFullWidth aligns='start'>
                <CommonLabelAndValueList
                  labelId={`last-name-${additionalContact.contactId}`}
                  label='Last name:'
                  childrenFallback={additionalContact.lastName}
                >
                  {
                    !isViewOnly &&
                    <FormInput
                      validationMessage={
                        validation.fieldErrors.lastName?.length
                          ? <ValidationMessageList messages={validation.fieldErrors.lastName} />
                          : undefined
                      }
                      onChange={lastName => additionalContact.lastName = lastName}
                      inputProps={{
                        'aria-labelledby': `last-name-${additionalContact.contactId}`,
                        value: additionalContact.lastName,
                        maxLength: 100,
                      }}
                    />
                  }
                </CommonLabelAndValueList>
                <CommonLabelAndValueList
                  labelId={`contact-email-${additionalContact.contactId}`}
                  label='Contact email:'
                  childrenFallback={additionalContact.emailAddress}
                >
                  {
                    !isViewOnly &&
                    <FormInput
                      validationMessage={
                        validation.fieldErrors.emailAddress?.length
                          ? <ValidationMessageList messages={validation.fieldErrors.emailAddress} />
                          : undefined
                      }
                      onChange={emailAddress => additionalContact.emailAddress = emailAddress}
                      inputProps={{
                        'aria-labelledby': `contact-email-${additionalContact.contactId}`,
                        value: additionalContact.emailAddress,
                        maxLength: 100,
                      }}
                    />
                  }
                </CommonLabelAndValueList>
              </FlexBox>
              <FlexBox isFullWidth aligns='start'>
                <CommonLabelAndValueList
                  labelId={`notification-options-${additionalContact.contactId}`}
                  label='Notification options:'
                  childrenFallback={getNotificationOptionLabel(additionalContact.contactNotificationTypeId)}
                >
                  {
                    !isViewOnly &&
                    <SelectOptions
                      onOptionSelect={(data) => {
                        if (!data.optionValue) return;
                        additionalContact.contactNotificationTypeId = +data.optionValue;
                      }}
                      validationMessage={
                        validation.fieldErrors.contactNotificationTypeId?.length
                          ? <ValidationMessageList messages={validation.fieldErrors.contactNotificationTypeId} />
                          : undefined
                      }
                      dropdownProps={{
                        'aria-labelledby': `notification-options-${additionalContact.contactId}`,
                        value: getNotificationOptionLabel(additionalContact.contactNotificationTypeId),
                        selectedOptions: [`${additionalContact.contactNotificationTypeId}`],
                      }}
                    >
                      <Option value={`${ContactNotificationType.AllNotifications}`}>All notifications</Option>
                      <Option value={`${ContactNotificationType.OnlyWinnerFinalistNotification}`}>Only winner/finalist notification</Option>
                    </SelectOptions>
                  }
                </CommonLabelAndValueList>
              </FlexBox>
              </FlexBox>
            );
          })
        }
      </FlexBox>
    </FlexBox>
  )
}

export const AdditionalContactDetailForm = observer(AdditionalContactDetailFormImpl);
